import { createLazyResource, createMutation } from '@magicdoorinc/ui';
import { createMemo, createSignal } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { LeaseTemplateRepository } from '~/repositories/leaseTemplateRepository';
import type { CreateLeaseTemplateDto } from '~/repositories/leaseTemplateRepository';
import type { CopyLeaseTemplateDto, UpdateLeaseTemplateDto } from '~/swagger/portal/Api';

const repo = new LeaseTemplateRepository();

export const [LeaseTemplateProvider, useLeaseTemplate] = createMagicDoorContext('LeaseTemplate', () => {
  const [leaseTemplates, { fetch, refetch, mutate }] = createLazyResource(async () => {
    return repo.getLeaseTemplates();
  });

  const [currentId, setCurrentId] = createSignal<string>();

  const current = createMemo(() => (currentId() ? leaseTemplates()?.find((t) => t.id === currentId()) : undefined));

  const addLeaseTemplate = createMutation(async (params: CreateLeaseTemplateDto) => {
    const created = await repo.createLeaseTemplate(params);
    mutate((prev) => [...(prev ?? []), created]);
    return created;
  });

  const updateLeaseTemplate = createMutation(async (leaseTemplateId: string, model: UpdateLeaseTemplateDto) => {
    const updated = await repo.updateLeaseTemplate(leaseTemplateId, model);
    mutate((prev) => prev?.map((t) => (t.id === leaseTemplateId ? updated : t)));
  });

  const deleteLeaseTemplate = createMutation(async (leaseTemplateId: string) => {
    await repo.deleteLeaseTemplate(leaseTemplateId);
    mutate((prev) => prev?.filter((t) => t.id !== leaseTemplateId));
  });

  const addLeaseTemplateAttachment = createMutation(async (leaseTemplateId: string, file: File) => {
    const created = await repo.addLeaseTemplateAttachment(leaseTemplateId, file);
    mutate((prev) => prev && [created, ...prev]);
  });

  const copyLeaseTemplate = createMutation(async (leaseTemplate: CopyLeaseTemplateDto) => {
    await repo.copyLeaseTemplate(leaseTemplate);
    refetch();
  });

  const isPdfEncrypted = createMutation(async (file: File) => {
    return repo.isPdfFileEncrypted(file);
  });

  return {
    get leaseTemplates() {
      fetch();
      return leaseTemplates;
    },
    get loading() {
      fetch();
      return () => leaseTemplates.loading;
    },
    refetch,
    setCurrentId,
    current,
    addLeaseTemplate,
    currentId,
    updateLeaseTemplate,
    addLeaseTemplateAttachment,
    deleteLeaseTemplate,
    copyLeaseTemplate,
    isPdfEncrypted,
  };
});
